import { useState, useCallback, memo, useRef, useEffect } from "react";
import { TextField, Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Plus, Minus } from "lucide-react";

// Debounce utility
const useDebounce = (callback: Function, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: any[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
};

const MemoizedTextField = memo(({ value, onChange, ...props }: any) => {
  const [localValue, setLocalValue] = useState(value);

  // Debounce the parent update
  const debouncedOnChange = useDebounce((newValue: string) => {
    onChange(newValue);
  }, 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  // Only update local value if parent value changes significantly
  useEffect(() => {
    if (value !== localValue && value !== undefined) {
      setLocalValue(value);
    }
  }, [value]);

  return (
    <TextField {...props} value={localValue || ""} onChange={handleChange} />
  );
});

const EditableFieldDisplay = memo(
  ({ label, value, onChange, type = "text", path }: any) => {
    const handleChange = useCallback(
      (newValue: any) => {
        onChange(path, newValue);
      },
      [onChange, path]
    );

    if (Array.isArray(value)) {
      return (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {label}
            </Typography>
            <Tooltip title="Add item">
              <IconButton
                size="small"
                onClick={() => handleChange([...value, ""])}
              >
                <Plus className="h-4 w-4" />
              </IconButton>
            </Tooltip>
          </Box>
          {value.map((item: string, index: number) => (
            <Box
              key={`${path}-${index}`}
              sx={{ display: "flex", gap: 1, mb: 1 }}
            >
              <MemoizedTextField
                fullWidth
                size="small"
                value={item}
                onChange={(newValue: string) => {
                  const newArray = [...value];
                  newArray[index] = newValue;
                  handleChange(newArray);
                }}
              />
              <Tooltip title="Remove item">
                <IconButton
                  size="small"
                  onClick={() => {
                    const newArray = value.filter(
                      (_: any, i: number) => i !== index
                    );
                    handleChange(newArray);
                  }}
                >
                  <Minus className="h-4 w-4" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        <MemoizedTextField
          fullWidth
          size="small"
          label={label}
          type={type}
          value={value}
          onChange={handleChange}
        />
      </Box>
    );
  }
);

const useEditableICP = (initialIcp: any) => {
  const [editedIcp, setEditedIcp] = useState(initialIcp);

  const handleFieldChange = useCallback((path: string, value: any) => {
    setEditedIcp((prev: any) => {
      const updateNestedValue = (
        obj: any,
        pathArray: string[],
        value: any
      ): any => {
        const key = pathArray[0];
        if (pathArray.length === 1) {
          return { ...obj, [key]: value };
        }
        return {
          ...obj,
          [key]: updateNestedValue(obj[key] || {}, pathArray.slice(1), value),
        };
      };

      const pathArray = path.split(".");
      return updateNestedValue(prev, pathArray, value);
    });
  }, []);

  return {
    editedIcp,
    setEditedIcp,
    handleFieldChange,
  };
};

export { EditableFieldDisplay, useEditableICP };
